/**
 * `exportPackage` (W1-07b formats-package; contract formats/types.ts `ExportPackageFn`; ARCHITECTURE.md §11,
 * DESIGN.md §5.11, §7.3; formats-and-packaging.md P§6.2):
 *
 *   lower (the IR the preview uses) -> encodeRpe -> info.yml / info.txt / extra.json / phiedit.json -> assets ->
 *   flat deterministic zip -> STRICT self-check -> result.
 *
 * Download name: "Title [Level].ext" (`Names.packageFileName`), the RPE id standing in for an empty title.
 * Names: the chart is `chart.json`, or `<rpeId>.json` with RPE naming (music and illustration then become
 * `<rpeId>.<ext>` too); assets keep their stored paths, made unique and safe again for the package
 * (`Names.sanitize`: never `export.json`, a name ending in `click.png`, `line.png`, a manifest name, or a hitsound
 * named exactly tap.mp3 / flick.mp3 / drag.mp3 — any asset a note uses as its hitsound counts as a hitsound). The
 * chart references assets by these export names (`LowerOptions.assetPath`), so references and files always agree.
 *
 * Files: info.yml, info.txt (when asked, RPE-compatible profile only: 'minimal' never writes it), the chart,
 * extra.json (when the project has effects, videos or an imported extra.json), then the assets in path order
 * (referenced ones; unused ones only with `includeUnusedAssets` or a backup; imported unknown files and fonts always
 * travel along), then phiedit.json (when asked, and always in a backup; it lists only the asset records the package
 * holds, `sidecarDoc`). Assets marked missing have no bytes and are not written (the checker blocks that export as
 * PH-01 / PH-02 unless the user exports anyway); the self-check, which fails on any other referenced file the
 * package lacks, is told about them (`SelfCheckOptions.knownMissing`).
 *
 * Chart-only: the chart bytes alone; for an rpe170 project `bakeSpeedForChartOnly` lowers speed in legacy mode
 * (eased speed baked to linear pieces) so the bare file plays the same under Phira's default integrator.
 * `rpeVersionOverride` replaces META.RPEVersion for this export only (written as an integer; never automatic). In
 * an RPE 1.7 speed project an override on the other side of 170 changes how eased speed integrates (Modern vs
 * Legacy-170 sub-mode), so the export would not play as previewed: the result then carries that warning in
 * `warnings` (the download needs confirmation; the self-check is unaffected) and `rpeVersionOverrideEffect` gives
 * the dialog's warning beforehand.
 * `infoOnlySwitches` lists what a bare chart.json cannot carry that differs from Phira's defaults.
 *
 * An encoder assertion (EncodeError) is an exporter bug: the result then carries no bytes and a failed self-check
 * naming the assertion, never a broken file. Reading an asset that the storage cannot provide rejects the export.
 */

import { createDocContainer } from '../../core/doc/container';
import { Lower } from '../../core/ir/lower';
import { tweenFields } from '../../core/ir/eventFields';
import type { IrChart, IrExtraJson, LowerReport } from '../../core/ir/types';
import type { AssetId, AssetRef, AssetRole, ChartDoc, DocIndexes, Key, TrackId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import { isNumericId } from '../../core/util/ids';
import type { MessageRef } from '../../core/util/types';
import { encodeExtra } from '../extra/encode';
import { writeInfoTxt } from '../info/infoTxt';
import { writeInfoYml } from '../info/infoYml';
import { resolvePackagePath } from '../info/resolve';
import { EncodeError } from '../json/types';
import type { EncodeExtraFn } from '../legacy/types';
import { encodeRpe } from '../rpe/encode';
import type { EncodeRpeFn } from '../rpe/json';
import type { RpeSpeedMode } from '../rpe/normalize';
import type {
  AssetSource,
  ExportOptions,
  ExportPackageFn,
  ExportResult,
  PackageFileEntry,
  SelfCheckResult,
} from '../types';
import { sha256Hex } from './hash';
import { infoFromDoc } from './manifestDoc';
import { Names, RESERVED_ROOT_NAMES } from './names';
import { selfCheckDetailed } from './selfCheck';
import type { SelfCheckOptions } from './selfCheck';
import { Sidecar, SIDECAR_FILE } from './sidecar';
import { zipSyncDeterministic } from './zip';

/** Replaceable steps (tests inject a corrupted encoder to prove the self-check catches it). */
export interface ExportDeps {
  readonly encodeRpe: EncodeRpeFn;
  readonly encodeExtra: EncodeExtraFn;
  readonly zip: (files: ReadonlyArray<{ readonly path: string; readonly bytes: Uint8Array }>) => Promise<Uint8Array>;
  readonly selfCheck: (
    bytes: Uint8Array,
    expected: { readonly chart: IrChart; readonly extra: IrExtraJson | null },
    opts?: SelfCheckOptions,
  ) => Promise<SelfCheckResult>;
}

const DEFAULT_DEPS: ExportDeps = {
  encodeRpe,
  encodeExtra,
  zip: (files) => Promise.resolve(zipSyncDeterministic(files)),
  selfCheck: selfCheckDetailed,
};

const I32_MAX = 2147483647;
const DEFAULT_ASPECT = Math.fround(16 / 9);
/** Roles of files that travel with the package even when nothing references them (imported unknown files). */
const ALWAYS_KEPT: ReadonlySet<AssetRole> = new Set(['other', 'font']);

function extOf(path: string): string {
  const base = path.slice(path.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? '' : base.slice(dot);
}

function isUsed(idx: DocIndexes, id: AssetId): boolean {
  const u = idx.assetUsers(id);
  return u.lines.length + u.notes.length + u.effects.length + u.videos.length + u.meta.length > 0;
}

interface NamePlan {
  readonly chart: string;
  readonly paths: ReadonlyMap<AssetId, string>;
  /** Assets whose bytes are written, in path order. */
  readonly included: readonly AssetRef[];
}

/** Export names of the chart and of every asset (see the module header). */
export function planNames(
  doc: ChartDoc,
  idx: DocIndexes,
  opts: Pick<ExportOptions, 'naming' | 'includeUnusedAssets' | 'target'>,
): NamePlan {
  const rpeId = doc.meta.rpeId;
  const rpeNaming = opts.naming === 'rpe-id' && isNumericId(rpeId);
  const chart = rpeNaming ? `${rpeId}.json` : 'chart.json';
  const taken = new Set<string>([...RESERVED_ROOT_NAMES, chart]);
  const paths = new Map<AssetId, string>();
  const assign = (asset: AssetRef, desired: string): void => {
    if (paths.has(asset.id)) return;
    const role: AssetRole = idx.assetUsers(asset.id).notes.length > 0 ? 'hitsound' : asset.role;
    const path = Names.sanitize(desired, taken, role);
    taken.add(path);
    paths.set(asset.id, path);
  };
  for (const field of ['music', 'illustration'] as const) {
    const id = doc.meta[field];
    const asset = id === null ? undefined : doc.assets.get(id);
    if (asset !== undefined) assign(asset, rpeNaming ? `${rpeId}${extOf(asset.path)}` : asset.path);
  }
  const rest = [...doc.assets.values()].sort((a, b) =>
    a.path < b.path ? -1 : a.path > b.path ? 1 : a.id < b.id ? -1 : 1,
  );
  for (const asset of rest) assign(asset, asset.path);
  const includeAll = opts.includeUnusedAssets || opts.target === 'backup';
  const included = rest
    .filter((a) => a.missing !== true && (includeAll || ALWAYS_KEPT.has(a.role) || isUsed(idx, a.id)))
    .sort((a, b) => {
      const x = paths.get(a.id) ?? '';
      const y = paths.get(b.id) ?? '';
      return x < y ? -1 : x > y ? 1 : 0;
    });
  return { chart, paths, included };
}

/**
 * The project phiedit.json describes: the document with the asset records of the files the package holds (and of
 * referenced files marked missing, which the chart still names). An unused file left out of the package
 * (`includeUnusedAssets` off) is left out of phiedit.json too, so restoring the package gives exactly the project
 * it describes; before, the restore dropped those records without a word and export -> import -> export changed
 * the package.
 */
function sidecarDoc(doc: ChartDoc, idx: DocIndexes, plan: NamePlan): ChartDoc {
  const written = new Set(plan.included.map((a) => a.id));
  const keep = (a: AssetRef): boolean => written.has(a.id) || (a.missing === true && isUsed(idx, a.id));
  if ([...doc.assets.values()].every(keep)) return doc;
  return { ...doc, assets: new Map([...doc.assets].filter(([, a]) => keep(a))) };
}

/** Package switches a bare chart.json cannot carry that differ from Phira's defaults. */
export function infoOnlySwitches(doc: ChartDoc, speedMode: RpeSpeedMode, hasExtra: boolean): MessageRef[] {
  const p = doc.pkg;
  const out: MessageRef[] = [];
  if (speedMode === 'rpe170') out.push({ key: 'pkg.switch.useRpe170Speed' });
  if (Math.fround(p.aspectRatio) !== DEFAULT_ASPECT) {
    out.push({ key: 'pkg.switch.aspectRatio', params: { value: Number(p.aspectRatio.toFixed(4)) } });
  }
  if (p.forceAspectRatio) out.push({ key: 'pkg.switch.forceAspectRatio' });
  if (Math.fround(p.lineLength) !== 6) out.push({ key: 'pkg.switch.lineLength', params: { value: p.lineLength } });
  if (Math.fround(p.backgroundDim) !== Math.fround(0.6)) {
    out.push({ key: 'pkg.switch.backgroundDim', params: { value: p.backgroundDim } });
  }
  if (p.holdPartialCover) out.push({ key: 'pkg.switch.holdPartialCover' });
  if (p.noteUniformScale) out.push({ key: 'pkg.switch.noteUniformScale' });
  if (!p.useAttachUiFix) out.push({ key: 'pkg.switch.useAttachUiFixOff' });
  if (p.infoOffsetSec !== 0) {
    out.push({ key: 'pkg.switch.infoOffset', params: { ms: Number((p.infoOffsetSec * 1000).toFixed(3)) } });
  }
  if (hasExtra) out.push({ key: 'pkg.switch.extraJson' });
  return out;
}

/**
 * Speed events whose integration depends on the RPE 1.7 sub-mode: easings other than step (0) and linear (1) between
 * two different speeds. With equal start and end speed both sub-modes integrate the constant speed (F§7.3: Modern
 * (e - s)·I(x) + s·x, Legacy-170 k = 0), whatever the easing.
 */
export function easedSpeedEventCount(chart: IrChart): number {
  let n = 0;
  for (const line of chart.judgeLineList) {
    for (const layer of line.eventLayers) {
      for (const e of layer?.speedEvents ?? []) if (e.easingType > 1 && e.start !== e.end) n++;
    }
  }
  return n;
}

/**
 * The easingType an rpe170-mode speed segment starting at `k` is written with (lowerTrack's rule): a tween segment
 * is one event with `tweenFields` (the model's tween id, or the imported raw easingType while Phira resolves it to
 * the same curve, e.g. 35 for a linear tween imported from an id the model cannot hold; a raw step 0 is never
 * restored on a ramp). Holds are constant events and beziers are baked to linear pieces (easingType 1 for both
 * here: a constant event integrates the same in both sub-modes whatever its easingType).
 */
function rpe170SpeedEasingType(k: Key): number {
  if (k.ease.kind !== 'tween') return 1;
  let src = k.src;
  if (src?.easingType === 0) {
    const { easingType: _step, ...rest } = src;
    src = rest;
  }
  return tweenFields(k.ease, src).easingType;
}

/**
 * Speed segments of a document whose integration depends on the RPE 1.7 sub-mode, counted on the speed tracks
 * without lowering (cheap enough for the export dialog to call on every change): segments between two different
 * speeds that are written as one speed event with an easingType above 1. Phira's 170 integrator sends every such
 * event through its eased path, whatever curve the id names: for a linear one (an imported raw id such as 35)
 * Legacy-170 has d0 = d1 and plays the constant average speed (s + e) / 2 while Modern integrates the ramp. Holds
 * are steps or constant events and beziers are baked to linear pieces in every mode, so neither depends on the
 * sub-mode, and neither does an eased segment whose speed does not change (F§7.3: both sub-modes then integrate the
 * constant speed s·x). Agrees with `easedSpeedEventCount` on the chart lowered in rpe170 mode.
 */
export function easedSpeedSegmentCount(doc: Pick<ChartDoc, 'tracks' | 'keys'>): number {
  const speedTracks = new Set<TrackId>();
  for (const t of doc.tracks.values()) if (t.owner.kind === 'line' && t.owner.prop === 'speed') speedTracks.add(t.id);
  if (speedTracks.size === 0) return 0;
  const byTrack = new Map<TrackId, Key[]>();
  for (const k of doc.keys.values()) {
    if (!speedTracks.has(k.track)) continue;
    const list = byTrack.get(k.track);
    if (list === undefined) byTrack.set(k.track, [k]);
    else list.push(k);
  }
  let n = 0;
  for (const keys of byTrack.values()) {
    if (keys.length < 2) continue;
    keys.sort((a, b) => Beats.cmp(a.t, b.t));
    // The last key starts no segment.
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i] as Key;
      const next = keys[i + 1] as Key;
      if (k.vOut !== next.vIn && rpe170SpeedEasingType(k) > 1) n++;
    }
  }
  return n;
}

/** Phira's RPE 1.7 speed sub-mode of an effective RPEVersion (F§7.3, rpe.rs:862-866). */
function subMode(rpeVersion: number): 'modern' | 'legacy170' {
  return rpeVersion >= 170 ? 'modern' : 'legacy170';
}

/**
 * What an RPE version override does to the playback of an RPE 1.7 speed project (preview = export): with
 * `useRpe170Speed` the effective RPEVersion picks how eased speed events integrate (>= 170 Modern: the speed follows
 * the curve; below: Legacy-170, speed = k·f'(x) + b). An override on the other side of 170 from the project's
 * version makes every eased speed event play differently from the preview. Returns the warning for the export
 * dialog (namespace 'pkg'), or null when the override changes nothing Phira plays (no override, a classic-speed
 * project or chart-only speed bake, the same sub-mode, or no eased speed event). Reads the document's speed tracks
 * only (no lowering), so the dialog can show it next to the version field on every change. `exportPackage` fails its
 * self-check on the same condition, so the download needs confirmation.
 */
export function rpeVersionOverrideEffect(
  doc: ChartDoc,
  opts: Pick<ExportOptions, 'rpeVersionOverride' | 'target' | 'bakeSpeedForChartOnly' | 'profile'>,
): MessageRef | null {
  if (opts.rpeVersionOverride === null || exportSpeedMode(doc, opts) !== 'rpe170') return null;
  return subModeChange(doc, opts.rpeVersionOverride);
}

/** The warning of `rpeVersionOverrideEffect` for an rpe170-mode export (null: nothing Phira plays changes). */
function subModeChange(doc: ChartDoc, override: number): MessageRef | null {
  const from = doc.pkg.rpeVersion;
  if (subMode(from) === subMode(override)) return null;
  const count = easedSpeedSegmentCount(doc);
  if (count === 0) return null;
  return { key: 'pkg.export.rpeVersionSubMode', params: { from, to: override, count, mode: subMode(override) } };
}

/** The speed mode an export lowers with (the chart-only speed bake lowers an rpe170 project in legacy mode). */
function exportSpeedMode(d: ChartDoc, opts: Pick<ExportOptions, 'target' | 'bakeSpeedForChartOnly'>): RpeSpeedMode {
  return opts.target === 'chartJson' && opts.bakeSpeedForChartOnly && d.pkg.speedMode === 'rpe170'
    ? 'legacy'
    : d.pkg.speedMode;
}

function withRpeVersion(chart: IrChart, override: number | null): IrChart {
  if (override === null) return chart;
  if (!Number.isInteger(override) || override < 0 || override > I32_MAX) {
    throw new RangeError(`exportPackage: rpeVersionOverride must be an integer from 0 to ${String(I32_MAX)}`);
  }
  return { ...chart, META: { ...chart.META, RPEVersion: override, rpeVersionEffective: override } };
}

const EMPTY_COUNTS = { lines: 0, events: 0, notes: 0 } as const;

/** The export pipeline with replaceable steps (see `ExportDeps`). */
export function createExportPackage(overrides: Partial<ExportDeps> = {}): ExportPackageFn {
  const deps: ExportDeps = { ...DEFAULT_DEPS, ...overrides };
  return async (doc: ChartDoc, assets: AssetSource, opts: ExportOptions): Promise<ExportResult> => {
    const container = createDocContainer(doc);
    const d = container.doc;
    const idx = container.idx;
    const plan = planNames(d, idx, opts);
    const speedMode = exportSpeedMode(d, opts);
    const lowered = Lower.lowerChart(d, idx, {
      profile: opts.profile,
      speedMode,
      speedBakeTolPx: d.pkg.speedBakeTolPx,
      assetPath: (id) => plan.paths.get(id) ?? d.assets.get(id)?.path ?? id,
    });
    const chart = withRpeVersion(lowered.chart, opts.rpeVersionOverride);
    // An override that moves eased speed into the other RPE 1.7 sub-mode plays differently from the preview.
    const subModeWarning =
      opts.rpeVersionOverride === null || speedMode !== 'rpe170' ? null : subModeChange(d, opts.rpeVersionOverride);
    const warnings: readonly MessageRef[] = subModeWarning === null ? [] : [subModeWarning];
    const chartOnly = opts.target === 'chartJson';
    const extraIr = chartOnly ? null : lowered.extra;
    const written = {
      rpeVersion: chart.META.rpeVersionEffective,
      useRpe170Speed: speedMode === 'rpe170',
      useAttachUiFix: d.pkg.useAttachUiFix,
    };
    const switches = infoOnlySwitches(d, speedMode, lowered.extra !== null);
    const ext = chartOnly ? 'json' : opts.target === 'zip' ? 'zip' : 'pez';
    // Without a title the download is named after the RPE id (language-neutral; RPE names packages so too).
    const fileName = Names.packageFileName(
      d.meta.name,
      d.meta.level,
      ext,
      isNumericId(d.meta.rpeId) ? d.meta.rpeId : undefined,
    );
    const mime = chartOnly ? 'application/json' : 'application/zip';
    const failed = async (e: EncodeError, lower: LowerReport): Promise<ExportResult> => {
      const empty = new Uint8Array(0);
      return {
        fileName,
        mime,
        bytes: empty,
        sha256: await sha256Hex(empty),
        files: [],
        lower,
        written,
        infoOnlySwitches: switches,
        warnings,
        selfCheck: { ok: false, diffs: [`encoder refused the chart: ${e.message}`], compared: EMPTY_COUNTS },
      };
    };

    const style = { pretty: opts.pretty, floatStyle: opts.floatStyle };
    let chartBytes: Uint8Array;
    let extraBytes: Uint8Array | null = null;
    try {
      chartBytes = deps.encodeRpe(chart, style);
      if (extraIr !== null) extraBytes = deps.encodeExtra(extraIr, opts.pretty);
    } catch (e) {
      if (e instanceof EncodeError) return failed(e, lowered.report);
      throw e;
    }

    if (chartOnly) {
      const selfCheck = await deps.selfCheck(chartBytes, { chart, extra: null }, { speedMode });
      return {
        fileName,
        mime,
        bytes: chartBytes,
        sha256: await sha256Hex(chartBytes),
        files: [{ path: plan.chart, bytes: chartBytes.length, kind: 'chart' }],
        lower: lowered.report,
        written,
        infoOnlySwitches: switches,
        warnings,
        selfCheck,
      };
    }

    const enc = new TextEncoder();
    const pathOf = (id: AssetId | null): string => (id === null ? '' : (plan.paths.get(id) ?? ''));
    const music = pathOf(d.meta.music);
    const illustration = pathOf(d.meta.illustration);
    const unlockVideo = d.meta.unlockVideo === null ? null : pathOf(d.meta.unlockVideo);
    const info = infoFromDoc(d, { chart: plan.chart, music, illustration, unlockVideo }, speedMode);
    const entries: Array<{ path: string; bytes: Uint8Array; kind: PackageFileEntry['kind'] }> = [
      { path: 'info.yml', bytes: enc.encode(writeInfoYml(info)), kind: 'info.yml' },
    ];
    // info.txt is part of the RPE-compatible profile only (ExportProfile: 'minimal' has no info.txt).
    if (opts.includeInfoTxt && opts.profile === 'rpe-compatible') {
      const txt = writeInfoTxt({
        Name: d.meta.name,
        Path: d.meta.rpeId,
        Song: music,
        Picture: illustration,
        Chart: plan.chart,
        Level: d.meta.level,
        Composer: d.meta.composer,
        Charter: d.meta.charter,
      });
      entries.push({ path: 'info.txt', bytes: enc.encode(txt), kind: 'info.txt' });
    }
    entries.push({ path: plan.chart, bytes: chartBytes, kind: 'chart' });
    if (extraBytes !== null) entries.push({ path: 'extra.json', bytes: extraBytes, kind: 'extra.json' });
    for (const asset of plan.included) {
      const path = plan.paths.get(asset.id);
      if (path === undefined) continue;
      let bytes: Uint8Array;
      try {
        bytes = await assets(asset.id);
      } catch (e) {
        throw new Error(
          `exportPackage: the file "${asset.path}" could not be read (${e instanceof Error ? e.message : String(e)})`,
          {
            cause: e,
          },
        );
      }
      entries.push({ path, bytes, kind: 'asset' });
    }
    if (opts.includeSidecar || opts.target === 'backup') {
      const project = sidecarDoc(doc, idx, plan);
      entries.push({ path: SIDECAR_FILE, bytes: Sidecar.write(project, await sha256Hex(chartBytes)), kind: 'sidecar' });
    }
    const bytes = await deps.zip(entries.map((e) => ({ path: e.path, bytes: e.bytes })));
    // Assets marked missing have no bytes to write (the checker's PH-01 / PH-02): references to them are known.
    const knownMissing = new Set<string>();
    for (const asset of d.assets.values()) {
      const path = asset.missing === true ? plan.paths.get(asset.id) : undefined;
      if (path !== undefined) knownMissing.add(resolvePackagePath(path) ?? path);
    }
    const selfCheck = await deps.selfCheck(bytes, { chart, extra: extraIr }, { knownMissing });
    return {
      fileName,
      mime,
      bytes,
      sha256: await sha256Hex(bytes),
      files: entries.map((e) => ({ path: e.path, bytes: e.bytes.length, kind: e.kind })),
      lower: lowered.report,
      written,
      infoOnlySwitches: switches,
      warnings,
      selfCheck,
    };
  };
}

export const exportPackage: ExportPackageFn = createExportPackage();
